import { useEffect, useRef, useState } from "react";
import {
  Bug,
  Camera,
  HeartHandshake,
  ImagePlus,
  Leaf,
  MessageCircle,
  PackageSearch,
  Send,
  Sprout,
  X,
} from "lucide-react";

const QUICK_ACTIONS = [
  { label: "Identify a plant", icon: Leaf },
  { label: "Disease or pest help", icon: Bug },
  { label: "Care guidance", icon: Sprout },
  { label: "Product questions", icon: PackageSearch },
  { label: "Seller assistance", icon: HeartHandshake },
];

export function PlantExpertDrawer() {
  const [open, setOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const closeButtonRef = useRef(null);
  const launcherRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      launcherRef.current?.focus();
    };
  }, [open]);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[70]" data-testid="plant-expert-drawer">
          <button
            type="button"
            className="absolute inset-0 h-full w-full cursor-default bg-black/40"
            onClick={() => setOpen(false)}
            aria-label="Close plant expert"
          />

          <aside
            id="plant-expert-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="plant-expert-title"
            aria-describedby="plant-expert-description"
            className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-[var(--gu-border)] bg-[var(--gu-bg)] shadow-2xl sm:w-[440px]"
          >
            <header className="flex items-center justify-between border-b border-[var(--gu-border)] p-6">
              <div>
                <div className="overline mb-1 text-[var(--gu-muted)]">Grace Under Umbrella</div>
                <h2 id="plant-expert-title" className="font-display text-2xl">Ask a Plant Expert</h2>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close plant expert"
                className="p-2 transition-colors hover:bg-[var(--gu-surface-2)] focus:outline-none focus:ring-2 focus:ring-[var(--gu-accent)]"
                data-testid="close-plant-expert"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="border border-[var(--gu-border)] bg-[var(--gu-surface)] p-5">
                <p id="plant-expert-description" className="font-display text-[1.7rem] leading-tight">
                  Hi! I’m Grace Under Umbrella’s botanical intelligence and business system.
                </p>
                <p className="mt-4 text-sm leading-6 text-[var(--gu-muted)]">
                  I’m here to help with plants, products, and becoming a seller. Choose a topic below to get started.
                </p>
              </div>

              <div className="mt-7">
                <p className="overline mb-3 text-[var(--gu-muted)]">How can I help?</p>
                <div className="grid gap-2">
                  {QUICK_ACTIONS.map(({ label, icon: Icon }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setSelectedAction(label)}
                      aria-pressed={selectedAction === label}
                      className={`flex items-center gap-3 border p-3.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--gu-accent)] ${
                        selectedAction === label
                          ? "border-[var(--gu-primary)] bg-[var(--gu-surface-2)]"
                          : "border-[var(--gu-border)] bg-[var(--gu-surface)] hover:bg-[var(--gu-surface-2)]"
                      }`}
                    >
                      <Icon size={18} strokeWidth={1.5} aria-hidden="true" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <p className="overline mb-3 text-[var(--gu-muted)]">Show me your plant</p>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" disabled className="flex items-center justify-center gap-2 border border-[var(--gu-border)] bg-[var(--gu-surface)] p-3 text-sm text-[var(--gu-muted)] opacity-70">
                    <ImagePlus size={17} strokeWidth={1.5} aria-hidden="true" /> Upload photo
                  </button>
                  <button type="button" disabled className="flex items-center justify-center gap-2 border border-[var(--gu-border)] bg-[var(--gu-surface)] p-3 text-sm text-[var(--gu-muted)] opacity-70">
                    <Camera size={17} strokeWidth={1.5} aria-hidden="true" /> Use camera
                  </button>
                </div>
                <p className="mt-2 text-xs leading-5 text-[var(--gu-muted)]">Photo and camera support are coming soon.</p>
              </div>
            </div>

            <footer className="border-t border-[var(--gu-border)] bg-[var(--gu-surface)] p-6">
              <div className="flex items-center gap-3 border border-[var(--gu-border)] bg-[var(--gu-bg)] px-4 py-3 text-[var(--gu-muted)]">
                <input
                  type="text"
                  disabled
                  value={selectedAction ? `${selectedAction} selected` : ""}
                  placeholder="Messaging coming soon"
                  aria-label="Plant expert messaging coming soon"
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--gu-muted)] disabled:cursor-not-allowed"
                />
                <Send size={17} strokeWidth={1.5} aria-hidden="true" />
              </div>
              <p className="mt-3 text-center text-xs text-[var(--gu-muted)]">
                The secure expert connection will be added in a future release.
              </p>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
}
